import { TypedDocumentNode, gql } from '@apollo/client';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import placeholder from 'assets/players/placeholder.png';
import { FullViewportScroll } from 'atoms/container/FullViewportScroll';
import { FontAwesomeIcon } from 'atoms/icons';
import { FullWidthContainer } from 'atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LinkBox, LinkOverlay } from 'atoms/navigation/Box';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { HeadlineXL, LabelM } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import { Chip } from 'atoms/ui/Chip';
import { proxyUrl } from 'atoms/ui/ResponsiveImg';
import { Follower } from 'components/player/PlayerPage/Header/Follower';
import { HeaderMetrics, MetricProps } from 'components/seo/HeaderMetrics';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useViewItem } from 'hooks/events/useViewItem';
import { glossary } from 'lib/glossary';
import { laptopAndAbove, tabletAndAbove } from 'style/mediaQuery';
import { createVar } from 'style/utils';

import {
  Header_dataPartner,
  Header_subscription,
} from './__generated__/index.graphql';

const backgroundImgVar = createVar();
const backgroundColorVar = createVar();

const Root = styled.div`
  position: relative;
  isolation: isolate;
  background-image: linear-gradient(
    to bottom,
    var(${backgroundColorVar}) 0%,
    transparent 100%
  );
`;
const StyledFullWidthContainer = styled(FullWidthContainer)`
  --container-padding: var(--double-unit);
`;
const Picture = styled.div`
  width: 100%;
  height: 450px;
  position: relative;
  &::before {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: var(${backgroundImgVar}) no-repeat;
    background-position: center var(--unit);
    background-size: 600px;
    mask-image: linear-gradient(to top, transparent 0%, black 40%);
    content: '';

    @media ${laptopAndAbove} {
      background-position: right calc(8 * var(--unit)) top var(--unit);
    }
  }

  .team & {
    --image-size: 120px;
    --padding-top: calc(6 * var(--unit));
    height: calc(var(--image-size) + var(--padding-top));
    &::before {
      background-size: auto var(--image-size);
      mask-image: initial;
      background-position: var(--double-unit) bottom;

      @media ${laptopAndAbove} {
        background-position: var(--double-unit) bottom;
      }
    }
  }
`;
const Subscription = styled.div`
  position: absolute;
  top: 0;
  right: var(--double-unit);
  z-index: 1;

  @media ${laptopAndAbove} {
    top: var(--double-unit);
  }
`;

const UpcomingGame = styled.div`
  width: 100%;
  z-index: 1;
  position: fixed;
  bottom: 0;
  transition: 0.3s ease bottom;
  background: var(--c-black);
  padding: var(--double-unit);
  border-top: 1px solid var(--c-nd-200);

  /* when signed in + on mobile, we hide the component because it takes too much space */
  &.signedIn {
    display: none;
  }

  @media ${laptopAndAbove} {
    position: absolute;
    border-top: none;
    background: transparent;
    bottom: var(--double-unit);
    width: calc(600px - var(--unit)); /* aligned with the picture background */
    padding-left: 0;
    padding-right: 0;
    right: 40px;

    &.signedIn {
      display: block;
    }

    & > div {
      margin: 0 auto;
    }

    /* Disable pointer events on this block to avoid covering clickable elements */
    pointer-events: none;
    /* Re-enable for buttons and links */
    & :is(a, button) {
      pointer-events: auto;
    }
  }
`;

const Content = styled(Vertical)`
  position: absolute;
  inset: auto 0 0 0;
  justify-content: end;

  @media ${laptopAndAbove} {
    padding-bottom: var(--triple-unit);
  }

  .team & {
    position: initial;
  }
`;
const StyledHeadlineXL = styled(HeadlineXL)`
  font-size: 48px;
  line-height: 1;

  &.small {
    font-size: 40px;
  }
  &.smaller {
    font-size: 32px;
  }

  @media ${laptopAndAbove} {
    .player & {
      max-width: 800px;
    }

    font-size: 120px;
    &.small {
      font-size: 110px;
    }
    &.smaller {
      font-size: 100px;
    }
  }
`;
const Memberships = styled(FullViewportScroll)`
  overflow-x: auto;
  padding-bottom: var(--half-unit);
  gap: var(--unit);

  @media ${tabletAndAbove} {
    padding-bottom: 0;
  }
`;
const MembershipImg = styled.img`
  width: auto;
  height: var(--double-unit);
`;

export type Props = {
  id: string;
  variant?: 'player' | 'team';
  pictureUrl: string | null;
  displayName: string;
  color?: string | null;
  memberships?: {
    name: string;
    pictureUrl?: string | null;
    to?: string;
    verified?: boolean;
  }[];
  scores?: MetricProps[];
  injury?: MetricProps;
  subscription?: Header_subscription;
  dataPartner?: Header_dataPartner | null;
  upcomingGame?: ReactNode;
};

export const Header = ({
  id,
  variant = 'player',
  pictureUrl,
  displayName,
  color,
  memberships,
  scores = [],
  injury,
  subscription,
  dataPartner,
  upcomingGame,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  useViewItem({ id });
  const metrics = [...scores];

  if (injury) metrics.push(injury);
  return (
    <Root
      className={classNames(variant)}
      style={{
        [backgroundColorVar]: color,
      }}
    >
      <Picture
        style={{
          [backgroundImgVar]: `url('${proxyUrl(pictureUrl || placeholder, {
            cropWidth: 480,
          })}')`,
        }}
      />
      {subscription && (
        <Subscription>
          <Follower subscription={subscription} dataPartner={dataPartner} />
        </Subscription>
      )}
      {upcomingGame && (
        <UpcomingGame className={classNames({ signedIn: !!currentUser })}>
          {upcomingGame}
        </UpcomingGame>
      )}
      <Content>
        <StyledFullWidthContainer>
          <Vertical gap={1.5}>
            <StyledHeadlineXL
              as="h1"
              className={classNames({
                small: displayName.length >= 20,
                smaller: displayName.length >= 40,
              })}
            >
              {displayName}
            </StyledHeadlineXL>
            {memberships && (
              <Memberships>
                {memberships.map(
                  ({
                    name,
                    pictureUrl: membershipPictureUrl,
                    to,
                    verified,
                  }) => (
                    <LinkBox key={name}>
                      <Chip size="medium">
                        <>
                          {membershipPictureUrl && (
                            <MembershipImg src={membershipPictureUrl} alt="" />
                          )}
                          {to ? (
                            <LinkOverlay as={Link} to={to}>
                              <Horizontal center gap={0.5}>
                                {name}
                                {verified && (
                                  <Tooltip
                                    title={
                                      <LabelM as="p">
                                        <FormattedMessage
                                          {...glossary.officiallyLicensed}
                                          values={{
                                            displayName: name,
                                            b: Bold,
                                          }}
                                        />
                                      </LabelM>
                                    }
                                    placement="top"
                                  >
                                    <FontAwesomeIcon icon={faBadgeCheck} />
                                  </Tooltip>
                                )}
                              </Horizontal>
                            </LinkOverlay>
                          ) : (
                            name
                          )}
                        </>
                      </Chip>
                    </LinkBox>
                  )
                )}
              </Memberships>
            )}
            <HeaderMetrics metrics={metrics} />
          </Vertical>
        </StyledFullWidthContainer>
      </Content>
    </Root>
  );
};

Header.fragments = {
  subscription: gql`
    fragment Header_subscription on WithSubscriptionsInterface {
      slug
      subscriptionsCount
      currentUserSubscription {
        slug
      }
      ...Follower_subscription
    }
    ${Follower.fragments.subscription}
  ` as TypedDocumentNode<Header_subscription>,
  dataPartner: gql`
    fragment Header_dataPartner on DataPartner {
      slug
      ...Follower_dataPartner
    }
    ${Follower.fragments.dataPartner}
  ` as TypedDocumentNode<Header_dataPartner>,
};
